import axios from 'axios';
import { supabase } from '../lib/supabaseClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Axios instance is kept exclusively for stateless utilities (YouTube scraping & GermanGrammarEngine)
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to award XP and track levels in the frontend
export const awardXP = async (amount, reason) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    
    // 1. Fetch current profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('xp, level')
      .eq('id', user.id)
      .single();
      
    const currentXp = profile?.xp || 0;
    const newXp = currentXp + amount;
    const newLevel = Math.max(1, Math.floor(newXp / 500) + 1);
    const today = new Date().toISOString().split('T')[0];
    
    // 2. Update profile
    await supabase
      .from('profiles')
      .update({
        xp: newXp,
        level: newLevel,
        last_activity_date: today
      })
      .eq('id', user.id);
      
    // 3. Log XP gain
    await supabase
      .from('xp_log')
      .insert({
        user_id: user.id,
        amount,
        reason
      });
      
    return { xp_awarded: amount, total_xp: newXp, level: newLevel };
  } catch (e) {
    console.error("Failed to award XP on database:", e);
    return null;
  }
};

// Helper to track daily streak
export const updateStreak = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profile } = await supabase
      .from('profiles')
      .select('streak, last_activity_date')
      .eq('id', user.id)
      .single();

    if (!profile) return;

    const today = new Date().toISOString().split('T')[0];
    const lastDate = profile.last_activity_date;

    if (lastDate === today) return;

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    let newStreak = 1;
    if (lastDate === yesterdayStr) {
      newStreak = (profile.streak || 0) + 1;
    }

    await supabase
      .from('profiles')
      .update({
        streak: newStreak,
        last_activity_date: today
      })
      .eq('id', user.id);
  } catch (e) {
    console.error("Failed to update daily login streak:", e);
  }
};

export const authAPI = {
  register: (userData) => supabase.auth.signUp({
    email: userData.email,
    password: userData.password,
    options: { data: { username: userData.username } }
  }),
  login: (credentials) => supabase.auth.signInWithPassword({
    email: credentials.email,
    password: credentials.password
  }),
  logout: () => supabase.auth.signOut(),
  getCurrentUser: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data: profile } = await supabase.from('profiles').select('*').eq('id', user.id).single();
    return {
      data: {
        id: user.id,
        email: user.email,
        username: profile?.username || user.email.split('@')[0],
        role: user.app_metadata?.role || 'user',
        profession: profile?.profession,
        xp: profile?.xp ?? 0,
        level: profile?.level ?? 1,
        streak: profile?.streak ?? 0
      }
    };
  },
  updateProfile: async (data) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No user session');
    const { error } = await supabase.from('profiles').update(data).eq('id', user.id);
    if (error) throw error;
    return { data: { status: 'success' } };
  },
  forgotPassword: (data) => supabase.auth.resetPasswordForEmail(data.email),
  resetPassword: (data) => supabase.auth.updateUser({ password: data.password }),
};

export const lessonsAPI = {
  getAll: () => supabase.from('lessons').select('*').order('lesson_number', { ascending: true }),
  getById: (id) => supabase.from('lessons').select('*').eq('id', id).single(),
};

export const exercisesAPI = {
  getByLesson: (lessonId) => supabase.from('exercises').select('*').eq('lesson_id', lessonId),
  submit: async (exerciseData) => {
    const { exercise_id, user_answer } = exerciseData;
    const { data: ex } = await supabase.from('exercises').select('*').eq('id', exercise_id).single();
    if (!ex) throw new Error("Exercise not found");

    const isCorrect = user_answer.trim().toLowerCase() === ex.correct_answer.trim().toLowerCase();
    
    let grammarErrors = [];
    if (ex.exercise_type === 'sentence_completion' && user_answer.includes(' ')) {
      // Call stateless PHP grammar checker engine
      const res = await api.post('/api/exercises/check-sentence', { sentence: user_answer });
      grammarErrors = res.data.grammar_errors || [];
    }

    let xpData = null;
    if (isCorrect) {
      xpData = await awardXP(10, 'exercise_completion');
    }

    return {
      data: {
        exercise_id,
        is_correct: isCorrect,
        user_answer,
        correct_answer: ex.correct_answer,
        feedback: isCorrect ? "Saktë! / Korrekt!" : `Gabim. Përgjigjja e saktë është: '${ex.correct_answer}'`,
        score: isCorrect ? 100 : 0,
        grammar_errors: grammarErrors,
        xp_awarded: xpData?.xp_awarded ?? 0
      }
    };
  },
  checkSentence: (sentence) => api.post('/api/exercises/check-sentence', { sentence }),
};

export const quizzesAPI = {
  getByLesson: async (lessonId) => {
    const { data: quiz } = await supabase.from('quizzes').select('*').eq('lesson_id', lessonId).single();
    if (!quiz) throw new Error("Quiz not found");
    const { data: questions } = await supabase.from('quiz_questions').select('id, question_text, question_type, options').eq('quiz_id', quiz.id);
    
    return {
      data: {
        ...quiz,
        questions: (questions || []).map(q => ({
          ...q,
          options: q.options ? q.options.split('|') : []
        }))
      }
    };
  },
  submit: async (quizData) => {
    const { quiz_id, answers, time_spent_seconds } = quizData;
    const { data: { user } } = await supabase.auth.getUser();
    
    // Fetch correct answers
    const { data: questions } = await supabase.from('quiz_questions').select('id, correct_answer').eq('quiz_id', quiz_id);
    let correctCount = 0;
    const total = questions.length;

    questions.forEach(q => {
      const userAns = (answers[q.id] || '').trim().toLowerCase();
      const correctAns = q.correct_answer.trim().toLowerCase();
      if (userAns === correctAns) correctCount++;
    });

    const score = total > 0 ? Math.round((correctCount / total) * 100) : 0;
    const passed = score >= 60;

    // Save result to Supabase
    await supabase.from('quiz_results').insert({
      user_id: user.id,
      quiz_id,
      score,
      total_questions: total,
      correct_answers: correctCount,
      time_taken: time_spent_seconds
    });

    const xpAmount = (correctCount * 10) + (passed ? 50 : 0);
    const xpData = await awardXP(xpAmount, 'quiz_completion');

    return {
      data: {
        score,
        total_questions: total,
        correct_answers: correctCount,
        passed,
        xp_awarded: xpData?.xp_awarded ?? 0,
        total_xp: xpData?.total_xp ?? 0,
        level: xpData?.level ?? 1
      }
    };
  },
};

export const examB2API = {
  getVariant: () => localStorage.getItem('b2Variant') || 'erwachsene',
  setVariant: (variant) => localStorage.setItem('b2Variant', variant),
  // These exam task loading endpoints query static json via stateless PHP server on Hostinger
  getInfo: () => api.get('/api/exam/b2/info'),
  getSkills: () => api.get('/api/exam/b2/skills'),
  getTasks: async (skill, variant) => {
    const v = variant || (localStorage.getItem('b2Variant') || 'erwachsene');
    const res = await api.get(`/api/exam/b2/${skill}`, { params: { variant: v } });
    // PHP backend returns: { success: true, data: { tasks: [...], redemittel?: {...} } }
    // Normalize to always return { tasks: [...], redemittel?: {...} }
    const payload = res.data?.data ?? res.data ?? {};
    return {
      tasks: payload.tasks ?? [],
      redemittel: payload.redemittel ?? null,
    };
  },
  getProgress: async (variant) => {
    const v = variant || (localStorage.getItem('b2Variant') || 'erwachsene');
    const { data: { user } } = await supabase.auth.getUser();
    const { data: attempts } = await supabase
      .from('exam_b2_attempts')
      .select('*')
      .eq('user_id', user.id)
      .eq('variant', v);
    return { data: attempts || [] };
  },
  getHistory: async (skill) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data: attempts } = await supabase
      .from('exam_b2_attempts')
      .select('*')
      .eq('user_id', user.id)
      .eq('skill', skill)
      .order('created_at', { ascending: false });
    return { data: attempts || [] };
  },
  submitLesen: async (answers, timeSpentSeconds, variant) => {
    const v = variant || (localStorage.getItem('b2Variant') || 'erwachsene');
    const { data: { user } } = await supabase.auth.getUser();
    
    // Evaluate score using local grading calculation
    const res = await api.get(`/api/exam/b2/lesen`, { params: { variant: v } });
    const tasks = res.data.data.tasks || [];
    let correct = 0;
    let total = 0;
    
    tasks.forEach(t => {
      (t.questions || []).forEach(q => {
        total++;
        if ((answers[q.id] || '').trim().toLowerCase() === q.correct_answer?.trim().toLowerCase()) {
          correct++;
        }
      });
    });

    const score = total > 0 ? Math.round((correct / total) * 100) : 0;
    const passed = score >= 60;

    await supabase.from('exam_b2_attempts').insert({
      user_id: user.id,
      skill: 'lesen',
      score,
      passed,
      answers: JSON.stringify(answers),
      time_spent_seconds: timeSpentSeconds,
      variant: v
    });

    const xpData = await awardXP(100, 'exam_lesen');

    return { data: { score, passed, xp_awarded: xpData?.xp_awarded ?? 0 } };
  },
  submitHoeren: async (answers, timeSpentSeconds, variant) => {
    const v = variant || (localStorage.getItem('b2Variant') || 'erwachsene');
    const { data: { user } } = await supabase.auth.getUser();
    
    const res = await api.get(`/api/exam/b2/hoeren`, { params: { variant: v } });
    const tasks = res.data.data.tasks || [];
    let correct = 0;
    let total = 0;
    
    tasks.forEach(t => {
      (t.questions || []).forEach(q => {
        total++;
        if ((answers[q.id] || '').trim().toLowerCase() === q.correct_answer?.trim().toLowerCase()) {
          correct++;
        }
      });
    });

    const score = total > 0 ? Math.round((correct / total) * 100) : 0;
    const passed = score >= 60;

    await supabase.from('exam_b2_attempts').insert({
      user_id: user.id,
      skill: 'hoeren',
      score,
      passed,
      answers: JSON.stringify(answers),
      time_spent_seconds: timeSpentSeconds,
      variant: v
    });

    const xpData = await awardXP(100, 'exam_hoeren');

    return { data: { score, passed, xp_awarded: xpData?.xp_awarded ?? 0 } };
  },
  submitSchreiben: async (taskId, text, timeSpentSeconds, variant) => {
    const v = variant || (localStorage.getItem('b2Variant') || 'erwachsene');
    const { data: { user } } = await supabase.auth.getUser();
    
    // Evaluate grammar check
    const grammarRes = await api.post('/api/exercises/check-sentence', { sentence: text });
    const score = grammarRes.data.score || 70;
    const passed = score >= 60;

    await supabase.from('exam_b2_attempts').insert({
      user_id: user.id,
      skill: 'schreiben',
      score,
      passed,
      answers: JSON.stringify({ text }),
      details: JSON.stringify(grammarRes.data),
      time_spent_seconds: timeSpentSeconds,
      variant: v
    });

    const xpData = await awardXP(150, 'exam_schreiben');

    return { data: { score, passed, xp_awarded: xpData?.xp_awarded ?? 0 } };
  },
  submitSprechen: async (payload) => {
    const v = payload?.variant || (localStorage.getItem('b2Variant') || 'erwachsene');
    const { data: { user } } = await supabase.auth.getUser();
    
    const score = 80; // Sprechen self-check fallback
    const passed = true;

    await supabase.from('exam_b2_attempts').insert({
      user_id: user.id,
      skill: 'sprechen',
      score,
      passed,
      answers: JSON.stringify(payload),
      time_spent_seconds: payload.timeSpentSeconds,
      variant: v
    });

    const xpData = await awardXP(150, 'exam_sprechen');

    return { data: { score, passed, xp_awarded: xpData?.xp_awarded ?? 0 } };
  },
};

export const dashboardAPI = {
  getStats: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No user session');
    
    // Update daily streak check on stats fetch
    await updateStreak();

    // 1. Fetch profile details
    const { data: profile } = await supabase.from('profiles').select('*').eq('id', user.id).single();
    
    // 2. Fetch vocabulary count
    const { count: totalWords } = await supabase.from('unknown_words').select('*', { count: 'exact', head: true }).eq('user_id', user.id);
    const { count: knownWords } = await supabase.from('unknown_words').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'known');
    const { count: learningWords } = await supabase.from('unknown_words').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'learning');
    const { count: srsDue } = await supabase.from('unknown_words').select('*', { count: 'exact', head: true }).eq('user_id', user.id).neq('status', 'known').lte('next_review_at', new Date().toISOString());

    // 3. Fetch monthly activity grouped by month
    const { data: xpLogs } = await supabase.from('xp_log').select('amount, created_at').eq('user_id', user.id);
    const monthsMap = {};
    (xpLogs || []).forEach(log => {
      const month = log.created_at.slice(0, 7); // YYYY-MM
      monthsMap[month] = (monthsMap[month] || 0) + log.amount;
    });
    const monthlyXp = Object.keys(monthsMap).sort().map(m => ({ month: m, xp_total: monthsMap[m] }));
    const activityDates = [...new Set((xpLogs || []).map((log) => log.created_at.slice(0, 10)))].sort();

    // 4. Fetch weak areas (from user progress with average score < 70)
    const { data: progress } = await supabase.from('user_progress').select('category, score').eq('user_id', user.id);
    const categoryTotals = {};
    (progress || []).forEach(p => {
      if (!categoryTotals[p.category]) categoryTotals[p.category] = { sum: 0, count: 0 };
      categoryTotals[p.category].sum += p.score;
      categoryTotals[p.category].count += 1;
    });
    const weakAreas = Object.keys(categoryTotals).map(cat => ({
      category: cat,
      avg_score: Math.round(categoryTotals[cat].sum / categoryTotals[cat].count),
      attempts: categoryTotals[cat].count
    })).filter(area => area.avg_score < 70).sort((a,b) => a.avg_score - b.avg_score).slice(0, 3);

    const successRate = totalWords > 0 ? Math.round((knownWords / totalWords) * 100) : 0;

    return {
      data: {
        xp: profile?.xp || 0,
        level: profile?.level || 1,
        streak: profile?.streak || 0,
        vocab: {
          total: totalWords || 0,
          known: knownWords || 0,
          learning: learningWords || 0,
          new: (totalWords || 0) - (knownWords || 0) - (learningWords || 0)
        },
        successRate,
        srs_due: srsDue || 0,
        monthly_xp: monthlyXp,
        activity_dates: activityDates,
        weak_areas: weakAreas
      }
    };
  }
};

export const leaderboardAPI = {
  get: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data: leaders } = await supabase
      .from('profiles')
      .select('id, username, xp, level, streak')
      .order('xp', { ascending: false })
      .limit(10);

    const processed = (leaders || []).map((l, index) => ({
      rank: index + 1,
      user_id: l.id,
      username: l.username,
      xp: l.xp,
      level: l.level,
      streak: l.streak,
      is_me: l.id === user?.id
    }));

    let myRank = null;
    if (user) {
      const match = processed.find(l => l.is_me);
      if (match) {
        myRank = match.rank;
      } else {
        const { data: profile } = await supabase.from('profiles').select('xp').eq('id', user.id).single();
        const { count } = await supabase.from('profiles').select('*', { count: 'exact', head: true }).gt('xp', profile?.xp || 0);
        myRank = (count || 0) + 1;
      }
    }

    return { data: { leaders: processed, my_rank: myRank } };
  }
};

export const unknownWordsAPI = {
  list: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data: words } = await supabase.from('unknown_words').select('*').eq('user_id', user.id).order('created_at', { ascending: false });
    return {
      data: {
        items: (words || []).map(w => ({
          id: w.id,
          word: w.word,
          context: w.context,
          source: w.source,
          level: w.level,
          notes: w.notes,
          status: w.status,
          createdAt: w.created_at
        }))
      }
    };
  },
  add: async (payload) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase.from('unknown_words').insert({
      user_id: user.id,
      word: payload.word,
      context: payload.context,
      source: payload.source,
      level: payload.level,
      notes: payload.notes,
      status: 'new'
    }).select().single();

    if (error) throw error;
    return { data: { ...data, id: data.id, createdAt: data.created_at } };
  },
  update: async (id, payload) => {
    const { data, error } = await supabase.from('unknown_words').update(payload).eq('id', id).select().single();
    if (error) throw error;
    return { data };
  },
  remove: async (id) => {
    const { error } = await supabase.from('unknown_words').delete().eq('id', id);
    if (error) throw error;
    return { data: { success: true } };
  },
};

export const srsAPI = {
  getDue: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const now = new Date().toISOString();
    const { data: words } = await supabase
      .from('unknown_words')
      .select('*')
      .eq('user_id', user.id)
      .neq('status', 'known')
      .or(`next_review_at.is.null,next_review_at.lte.${now}`)
      .order('next_review_at', { ascending: true })
      .limit(20);

    return { data: { due: words || [], count: words?.length || 0 } };
  },
  review: async (wordId, rating) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data: word } = await supabase.from('unknown_words').select('*').eq('id', wordId).eq('user_id', user.id).single();
    if (!word) throw new Error("Word not found");

    let ef = parseFloat(word.ease_factor || 2.5);
    let interval = parseInt(word.review_interval || 1);
    let count = parseInt(word.review_count || 0) + 1;
    let newStatus = 'learning';

    if (rating >= 3) {
      if (count === 1) interval = 1;
      else if (count === 2) interval = 6;
      else interval = Math.round(interval * ef);

      ef = Math.max(1.3, ef + (0.1 - (5 - rating) * (0.08 + (5 - rating) * 0.02)));
      newStatus = (rating >= 4 && count >= 3) ? 'known' : 'learning';
    } else {
      interval = 1;
      ef = Math.max(1.3, ef - 0.2);
      newStatus = 'learning';
    }

    const nextReview = new Date();
    nextReview.setDate(nextReview.getDate() + interval);

    const { error } = await supabase
      .from('unknown_words')
      .update({
        status: newStatus,
        ease_factor: ef,
        review_interval: interval,
        review_count: count,
        next_review_at: nextReview.toISOString()
      })
      .eq('id', wordId)
      .select()
      .single();

    if (error) throw error;

    const xpAwarded = rating >= 3 ? 5 : 2;
    const xpData = await awardXP(xpAwarded, 'srs_review');

    return {
      data: {
        word_id: wordId,
        new_status: newStatus,
        next_review_at: nextReview.toISOString(),
        interval_days: interval,
        xp_awarded: xpAwarded,
        total_xp: xpData?.total_xp ?? 0
      }
    };
  },
};

export const bookmarksAPI = {
  list: async (type) => {
    const { data: { user } } = await supabase.auth.getUser();
    let query = supabase.from('bookmarks').select('*').eq('user_id', user.id);
    if (type) query = query.eq('content_type', type);
    const { data } = await query.order('created_at', { ascending: false });
    return { data: { bookmarks: data || [] } };
  },
  add: async (contentType, contentId, contentTitle) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase.from('bookmarks').insert({
      user_id: user.id,
      content_type: contentType,
      content_id: contentId,
      content_title: contentTitle
    }).select().single();

    if (error) throw error;
    return { data };
  },
  remove: async (id) => {
    const { error } = await supabase.from('bookmarks').delete().eq('id', id);
    if (error) throw error;
    return { data: { success: true } };
  },
};

export const journalAPI = {
  list: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data } = await supabase.from('journal_entries').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(30);
    return {
      data: {
        entries: (data || []).map(e => ({
          ...e,
          errors: e.errors ? JSON.parse(e.errors) : []
        }))
      }
    };
  },
  submit: async (text) => {
    const { data: { user } } = await supabase.auth.getUser();
    
    // Call stateless Hostinger grammar checker endpoint
    const grammarRes = await api.post('/api/exercises/check-sentence', { sentence: text });
    const score = grammarRes.data.score || 100;
    const errors = grammarRes.data.grammar_errors || [];

    const xpAmount = Math.max(5, Math.round((score / 100) * 30));
    const xpData = await awardXP(xpAmount, 'journal_entry');

    const { data, error } = await supabase.from('journal_entries').insert({
      user_id: user.id,
      text,
      score,
      errors: JSON.stringify(errors),
      xp_awarded: xpAmount
    }).select().single();

    if (error) throw error;

    return {
      data: {
        ...data,
        errors,
        total_xp: xpData?.total_xp ?? 0
      }
    };
  },
};

export const challengeAPI = {
  getQuestions: async () => {
    // Stateless random questions query from Supabase questions table
    const { data } = await supabase.from('quiz_questions').select('id, question_text, question_type, options, correct_answer');
    
    // Shuffle client side to mimic randomness
    const shuffled = (data || []).sort(() => 0.5 - Math.random()).slice(0, 20);
    
    return {
      data: {
        questions: shuffled.map(q => ({
          ...q,
          options: q.options ? q.options.split('|') : []
        }))
      }
    };
  },
  submit: async (answers, timeSeconds) => {
    let correct = 0;
    const total = answers.length;
    answers.forEach(a => {
      if (a.user_answer.trim().toLowerCase() === a.correct_answer.trim().toLowerCase()) {
        correct++;
      }
    });

    const score = total > 0 ? Math.round((correct / total) * 100) : 0;
    const xpAmount = correct * 5;
    const xpData = await awardXP(xpAmount, 'timed_challenge');

    return {
      data: {
        correct,
        total,
        score,
        xp_awarded: xpAmount,
        total_xp: xpData?.total_xp ?? 0,
        level: xpData?.level ?? 1
      }
    };
  },
};

export const youtubeSubtitlesAPI = {
  fetchTranscript: (videoId) => api.get(`/api/youtube-subtitles/${videoId}`),
};

export default api;
